"use client"

import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react"
import Image from "next/image"
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react"

import { cn } from "@/lib/utils"
import type { ReferenceImage } from "@/data/grass-reference-photos"
import { trackEvent } from "@/lib/analytics"

interface ReferencePhotoGalleryProps {
  /** The user's uploaded grass photo (data URL / object URL). */
  uploadedImage: string
  /** Lead reference image first, then curated alternates (blade, seed head, …). */
  images: ReferenceImage[]
  /** Display name of the matched species, e.g. "Kentucky Bluegrass". */
  grassType: string
  /**
   * Peak-CTA layout is up: shrink the pair to verification-sized thumbnails on
   * mobile so the save ask lands a half-scroll away (desktop keeps full width).
   */
  showPeakCta: boolean
}

/**
 * Side-by-side "your photo vs reference" with a multi-image reference gallery.
 *
 * The reference slot's job is visual verification ("does my lawn look like
 * that?"), so we lead with the turf-mat shot and let the user browse curated
 * alternates (blade close-up, seed head) — recognition over recall, and a
 * richer, more trustworthy match than one ambiguous frame (see
 * obsidian/research/growth-design/amber-alert.md).
 *
 * Inline the slot is a single image: tapping it opens a full-size lightbox, and
 * the alternates are reached there by swipe, arrow key, or on-screen chevron.
 * The inline layout sits directly above the save ask on the result, so the
 * alternates are not worth two rows of height there. Single-image species
 * render with no gallery chrome.
 *
 * Attribution for every shown image is parked at the bottom of the result card
 * by the parent (CC BY-SA is satisfied by same-page credit), so this component
 * shows captions, not credits.
 */
export function ReferencePhotoGallery({
  uploadedImage,
  images,
  grassType,
  showPeakCta,
}: ReferencePhotoGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const hasAlternates = images.length > 1
  // Guard against an out-of-range index if `images` ever shrinks.
  const active = images[activeIndex] ?? images[0]

  const select = useCallback(
    (index: number, source: "arrow" | "swipe") => {
      setActiveIndex(index)
      trackEvent("reference_gallery_view", {
        grass_type: grassType,
        image_index: index,
        caption: images[index]?.caption ?? undefined,
        source,
      })
    },
    [grassType, images],
  )

  const step = useCallback(
    (delta: number, source: "arrow" | "swipe" = "arrow") => {
      const next = (activeIndex + delta + images.length) % images.length
      select(next, source)
    },
    [activeIndex, images.length, select],
  )

  // Horizontal swipe between reference images, the primary way through the set
  // on touch now that the thumbnail strip is gone. Bound to the image itself
  // rather than the backdrop, which owns tap-to-close.
  const touchStartX = useRef<number | null>(null)
  const onTouchStart = useCallback((e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null
  }, [])
  const onTouchEnd = useCallback(
    (e: ReactTouchEvent) => {
      const startX = touchStartX.current
      touchStartX.current = null
      if (startX === null || !hasAlternates) return
      const deltaX = (e.changedTouches[0]?.clientX ?? startX) - startX
      // Below this a drag is indistinguishable from a tap or a scroll nudge.
      if (Math.abs(deltaX) < 40) return
      step(deltaX < 0 ? 1 : -1, "swipe")
    },
    [hasAlternates, step],
  )

  const openLightbox = useCallback(() => {
    setLightboxOpen(true)
    trackEvent("reference_gallery_zoom", {
      grass_type: grassType,
      image_index: activeIndex,
    })
  }, [grassType, activeIndex])

  // Lightbox keyboard controls + body scroll lock while open.
  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false)
      else if (e.key === "ArrowRight" && hasAlternates) step(1)
      else if (e.key === "ArrowLeft" && hasAlternates) step(-1)
    }
    document.addEventListener("keydown", onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [lightboxOpen, hasAlternates, step])

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-2 gap-2 md:gap-3 w-full",
          showPeakCta && "max-w-56 mx-auto md:max-w-none",
        )}
      >
        {/* Your photo */}
        <figure className="m-0">
          <div
            className="aspect-square bg-linear-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden shadow-md"
            style={{ viewTransitionName: "grass-photo" }}
          >
            {uploadedImage && (
              <img
                src={uploadedImage}
                alt="Your grass photo"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <figcaption className="mt-1.5 text-[11px] font-semibold text-gray-500 text-center">
            Your photo
          </figcaption>
        </figure>

        {/* Reference gallery */}
        <figure className="m-0">
          <button
            type="button"
            onClick={openLightbox}
            aria-label={
              hasAlternates
                ? `Enlarge ${images.length} reference photos of ${grassType}`
                : `Enlarge reference photo of ${grassType}${active.caption ? ` — ${active.caption}` : ""}`
            }
            className="group relative block w-full aspect-square bg-linear-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden shadow-md cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
          >
            <Image
              key={active.src}
              src={active.src}
              alt={`Reference photo of ${grassType}${active.caption ? ` — ${active.caption}` : ""}`}
              fill
              sizes="(max-width: 768px) 50vw, 200px"
              className="object-cover"
            />
            {/* Zoom affordance. With the thumbnail strip gone this badge is the
                only cue that alternates exist, so it stays visible instead of
                waiting for hover whenever there is more than one image. */}
            <span
              className={cn(
                "absolute top-1.5 right-1.5 flex items-center gap-1 h-6 px-1.5 rounded-md bg-black/45 text-white transition-opacity",
                hasAlternates
                  ? "opacity-90"
                  : "opacity-90 md:opacity-0 md:group-hover:opacity-100",
              )}
            >
              <Maximize2 className="w-3.5 h-3.5" />
              {hasAlternates && (
                <span className="text-[10px] font-semibold leading-none">{images.length}</span>
              )}
            </span>
          </button>

          <figcaption className="mt-1.5 text-[11px] font-semibold text-gray-500 text-center">
            {showPeakCta ? (
              // Compact thumbnails can't fit the full species name on one line.
              <>
                <span className="md:hidden">Reference</span>
                <span className="hidden md:inline">Reference: {grassType}</span>
              </>
            ) : (
              <>Reference: {grassType}</>
            )}
          </figcaption>

          {/* The per-image caption and the thumbnail strip both moved into the
              lightbox. Inline they cost two rows of height directly above the
              save ask, and the alternates are browsable by swipe/arrow once the
              lightbox is open. */}
        </figure>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Reference photo of ${grassType}`}
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-4 animate-in fade-in duration-150"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
            className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev / next */}
          {hasAlternates && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  step(-1)
                }}
                aria-label="Previous photo"
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  step(1)
                }}
                aria-label="Next photo"
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image (object-contain so nothing is cropped in the zoom view). */}
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className="relative w-full max-w-2xl aspect-square touch-pan-y"
          >
            <Image
              src={active.src}
              alt={`Reference photo of ${grassType}${active.caption ? ` — ${active.caption}` : ""}`}
              fill
              sizes="(max-width: 768px) 90vw, 640px"
              className="object-contain"
            />
          </div>

          <div className="mt-4 text-center" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-white">
              Reference: {grassType}
            </p>
            {active.caption && (
              <p className="mt-0.5 text-xs text-white/70">{active.caption}</p>
            )}
            {active.credit && (
              <p className="mt-1 text-[10px] text-white/50">
                {active.credit}{" "}
                <a
                  href={active.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="underline hover:text-white/70"
                  onClick={(e) => e.stopPropagation()}
                >
                  {active.license}
                </a>
              </p>
            )}
            {hasAlternates && (
              <p className="mt-2 text-[10px] text-white/40">
                {activeIndex + 1} / {images.length}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}
